import { captureException } from "@sentry/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { LoginCard } from "@/components/auth/login-card";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { claimSocket } from "@/lib/socket-owner";
import { authStore } from "../lib/stores/auth-store";

export const Route = createFileRoute("/")({
	component: HomeComponent,
});

function HomeComponent() {
	const navigate = useNavigate();
	const user = useStore(authStore, (state) => state.user);
	const wsConnected = useStore(authStore, (state) => state.wsConnected);
	const login = useStore(authStore, (state) => state.login);
	const logout = useStore(authStore, (state) => state.logout);

	// This login drives the `/app` session, so this surface owns the socket while
	// it is mounted (it may be arriving from `/operations`, which held it).
	useEffect(() => {
		claimSocket("app").catch((error) => {
			console.error("Failed to claim the socket for /app:", error);
		});
	}, []);

	useEffect(() => {
		if (user) {
			const pendingInviteCode = sessionStorage.getItem("pendingInviteCode");
			if (pendingInviteCode) {
				if (wsConnected) {
					sessionStorage.removeItem("pendingInviteCode");
					navigate({
						to: "/invite/$code",
						params: { code: pendingInviteCode },
					});
				}
			} else {
				navigate({ to: "/app" });
			}
		}
	}, [user, wsConnected, navigate]);

	const handleLogout = async () => {
		try {
			await logout();
			toast.success("Logged out successfully!");
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "auth_logout" } });
			const errorMessage =
				error instanceof Error ? error.message : "Logout failed";
			toast.error(errorMessage);
		}
	};

	const hasPendingInvite =
		user && sessionStorage.getItem("pendingInviteCode") && !wsConnected;

	if (hasPendingInvite) {
		return (
			<div className="flex min-h-screen items-center justify-center p-4">
				<div className="flex flex-col items-center gap-4">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<div className="w-full max-w-md">
				{user ? (
					<Card>
						<CardHeader>
							<CardTitle>Welcome back!</CardTitle>
							<CardDescription>
								You are logged in as <strong>{user.email}</strong>
							</CardDescription>
						</CardHeader>
						<CardFooter className="flex gap-2">
							<Button
								className="flex-1"
								onClick={() => navigate({ to: "/app" })}
								type="button"
							>
								Go to App
							</Button>
							<Button onClick={handleLogout} type="button" variant="outline">
								Logout
							</Button>
						</CardFooter>
					</Card>
				) : (
					<LoginCard
						description="Enter your credentials to access your account"
						onSubmit={login}
						sentryContext="auth_login"
						title="Welcome back"
					/>
				)}
			</div>
		</div>
	);
}
